/* ===========================================================
   키즈다 뉴스 페이지 (라우트 기반)
   - 사이트 헤더/푸터는 프로젝트 공통 컴포넌트 사용
   - 카테고리 메뉴는 categoryList API(codeType:"TYPES", type:"NEWS")로 구성
     ("전체"는 맨 앞에 유지 / API 실패 시 정적 카테고리로 폴백)
   - 본문(메인/카테고리/상세/오늘전체/검색)은 URL 경로로 분리
       /kizda_news                     → 메인(홈)
       /kizda_news/category/:corner    → 카테고리 리스트 페이지
       /kizda_news/article/:id         → 기사 상세 페이지
       /kizda_news/today/:date         → 오늘 뉴스 전체 페이지
       /kizda_news/search?q=...        → 검색 결과 페이지
   =========================================================== */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";

import "../../assets/css/kizda_news.scss";
import Header from "../../components/header";
import NewsFooter from "../../components/news_footer";

import {
  approvedArticles,
  CORNERS,
  normalizeCat,
  articleCategoryName,
} from "./data";
import { getNewsCategoryList } from "./category_api";
import { getNewsList, getPickNews, getNewsDetail, getPostPrevNext, createWebPostRead } from "./news_api";
import { getNewsTopBanner, getLandingBanners } from "./banner_api";
import {
  CornerNav,
  TodayNews,
  ApiCornerBlock,
  SubscribeCTA,
  SearchResults,
  TodayAllPage,
  CornerPage,
  ArticlePage,
  SideAdRail,
  TopAdBanner,
  TODAY,
  setViewCountInCache,
} from "./news_components";

/* 홈(키즈다 뉴스/PICK/배너) 세션 메모리 캐시 — 상세 갔다가 뒤로가기 시
   데이터를 다시 불러오지 않고 즉시 복원해 페이지 높이를 유지(스크롤 복원 정확도↑). */
const _homeCache = { byDate: new Map(), banner: undefined, landingBanners: undefined };
// 진행 중인 요청 공유 → 중복 호출 방지 (StrictMode 이중 실행/재마운트 대비)
const _homeInflight = new Map(); // newsDate -> Promise<{newsItems,popularItems,recommendItems}>
let _bannerInflight = null; // Promise<banner>
let _landingBannerInflight = null; // Promise<banners[]>
// 카테고리 목록도 캐시 — 재마운트 시 정적 CORNERS로 리셋되어 섹션이 재로딩되는 것 방지
let _categoriesCache = null;

/* 잘못된 경로(없는 기사/카테고리)용 안내 */
function NotFoundBody({ label, onBack }) {
  return (
    <section style={{ padding: "60px 0", textAlign: "center", color: "var(--muted)" }}>
      <div style={{ fontSize: 18, fontWeight: 800, color: "var(--ink-2)" }}>{label}을(를) 찾을 수 없어요</div>
      <div style={{ fontSize: 14, margin: "8px 0 22px" }}>주소가 바뀌었거나 삭제되었을 수 있어요.</div>
      <button onClick={onBack} className="kd-cta">키즈다 뉴스 홈으로</button>
    </section>
  );
}

function KizdaNewsPage() {
  const articles = approvedArticles;
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();

  // 카테고리: categoryList API → 실패 시 정적 CORNERS 폴백
  // 캐시가 있으면 즉시 복원 (재마운트/뒤로가기 시 리셋·재호출 방지)
  const [categories, setCategories] = useState(_categoriesCache ?? CORNERS);
  // 카테고리 목록 로딩 완료 여부 — 섹션은 로딩 완료 후에만 렌더(정적 키로 미리 API 호출되는 것 방지)
  const [categoriesReady, setCategoriesReady] = useState(!!_categoriesCache);
  useEffect(() => {
    if (_categoriesCache) {
      setCategoriesReady(true);
      return; // 이미 로드됨
    }
    let alive = true;
    getNewsCategoryList().then((list) => {
      if (!alive) return;
      if (list && list.length) {
        _categoriesCache = list;
        setCategories(list);
      }
      setCategoriesReady(true); // 성공/실패 무관 — 이후 섹션 렌더 허용
    });
    return () => {
      alive = false;
    };
  }, []);

  // "키즈다 뉴스" 섹션 실데이터: 최신순 리스트 + 인기/추천 PICK + 상단 배너
  // (모두 API 실패 시 null → TodayNews가 정적 데이터로 폴백)
  // 날짜 상태를 상위에서 관리 → 날짜 변경 시 뉴스/PICK을 서버에서 재조회
  const [newsDate, setNewsDate] = useState(TODAY);
  // 초기 상태를 캐시에서 즉시 복원 (재마운트/뒤로가기 시 빈 화면·재호출 방지)
  const _initHome = _homeCache.byDate.get(TODAY);
  const [newsItems, setNewsItems] = useState(_initHome?.newsItems ?? null);
  const [popularItems, setPopularItems] = useState(_initHome?.popularItems ?? null);
  const [recommendItems, setRecommendItems] = useState(_initHome?.recommendItems ?? null);
  // 헤드라인 배너 목록(배열) — 1개면 단일, 2개 이상이면 TodayNews에서 슬라이드
  const [newsBanners, setNewsBanners] = useState(_homeCache.banner ?? []);
  // 랜딩 배너(우측 세로/상단 가로) — searchWebBannersWithFiles
  const [landingBanners, setLandingBanners] = useState(_homeCache.landingBanners ?? []);
  const [landingBannersLoading, setLandingBannersLoading] = useState(
    _homeCache.landingBanners === undefined
  );
  const [newsLoading, setNewsLoading] = useState(_initHome == null); // 첫 로딩 기본화면용

  const path = location.pathname;
  const isArticle = path.startsWith("/kizda_news/article/");
  const isCategory = path.startsWith("/kizda_news/category/");
  const isTodayAll = path.startsWith("/kizda_news/today/");
  const isSearch = path.startsWith("/kizda_news/search");
  const isHome = !isArticle && !isCategory && !isTodayAll && !isSearch;

  // 배너는 날짜와 무관 → 세션 중 1회만 조회 (캐시에 있으면 복원, 재호출 안 함)
  useEffect(() => {
    if (!isHome) return;
    if (_homeCache.banner !== undefined) {
      setNewsBanners(_homeCache.banner);
      return;
    }
    let alive = true;
    // 진행 중인 배너 요청이 있으면 공유 (중복 호출 방지)
    if (!_bannerInflight) {
      _bannerInflight = getNewsTopBanner().then((banners) => {
        _homeCache.banner = Array.isArray(banners) ? banners : []; // 완료 후 캐시 기록
        return _homeCache.banner;
      });
      _bannerInflight.finally(() => {
        _bannerInflight = null;
      });
    }
    _bannerInflight.then((banners) => {
      if (alive) setNewsBanners(banners);
    });
    return () => {
      alive = false;
    };
  }, [isHome]);

  // 랜딩 배너(세로/가로)도 세션 중 1회만 조회 (캐시/중복 호출 방지 동일 패턴)
  useEffect(() => {
    if (!isHome) return;
    if (_homeCache.landingBanners !== undefined) {
      setLandingBanners(_homeCache.landingBanners);
      setLandingBannersLoading(false);
      return;
    }
    let alive = true;
    setLandingBannersLoading(true);
    if (!_landingBannerInflight) {
      _landingBannerInflight = getLandingBanners().then((list) => {
        _homeCache.landingBanners = list ?? [];
        return _homeCache.landingBanners;
      });
      _landingBannerInflight.finally(() => {
        _landingBannerInflight = null;
      });
    }
    _landingBannerInflight.then((list) => {
      if (!alive) return;
      setLandingBanners(list);
      setLandingBannersLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [isHome]);

  // 뉴스 리스트 + 인기/추천 PICK → 선택 날짜(newsDate)에 따라 재조회.
  // 선택한 날짜는 달력/제목에 그대로 유지하고(동기화 안 함),
  // 그 날짜에 데이터가 없으면(예: 주말) 데이터만 가장 가까운 이전 날짜 분을 조회해 보여준다.
  //  1) getNewsList(endDate=선택일)로 "선택일 이하 최신" 뉴스를 받고
  //  2) 그중 가장 최신 기사 날짜(resolved)의 기사만 추려 섹션에 표시
  //  3) PICK도 동일하게 resolved 날짜로 조회
  // 같은 날짜로 이미 조회했으면(캐시 존재) 재호출하지 않고 복원 (상세→뒤로가기 시 초기화/재호출 방지)
  useEffect(() => {
    if (!isHome) return;
    const cached = _homeCache.byDate.get(newsDate);
    if (cached) {
      setNewsItems(cached.newsItems);
      setPopularItems(cached.popularItems);
      setRecommendItems(cached.recommendItems);
      setNewsLoading(false);
      return;
    }
    let alive = true;
    setNewsLoading(true);

    // 진행 중인 동일 날짜 요청이 있으면 공유 (StrictMode/재마운트 중복 호출 방지)
    let p = _homeInflight.get(newsDate);
    if (!p) {
      const dateParam = newsDate === TODAY ? "" : newsDate;
      p = getNewsList({ size: 20, date: dateParam }).then(async (list) => {
        // 실제 데이터가 있는 가장 가까운 날짜 (선택일 또는 그 이전)
        const resolved = Array.isArray(list) && list.length ? list[0].date : "";
        // 선택 날짜는 그대로 두고, 데이터만 resolved 날짜 분을 표시
        const items = resolved ? list.filter((a) => a.date === resolved) : list;
        // PICK도 섹션과 같은 날짜(resolved)로 조회 (없으면 기본 최신)
        const pickDate = resolved || "";
        const [popular, recommend] = await Promise.all([
          getPickNews("popular", { size: 5, date: pickDate }),
          getPickNews("recommend", { size: 8, date: pickDate }),
        ]);
        const result = { newsItems: items, popularItems: popular, recommendItems: recommend };
        _homeCache.byDate.set(newsDate, result); // 완료 후 캐시 기록
        return result;
      });
      _homeInflight.set(newsDate, p);
      p.finally(() => _homeInflight.delete(newsDate));
    }

    p.then((result) => {
      if (!alive) return;
      setNewsItems(result.newsItems);
      setPopularItems(result.popularItems);
      setRecommendItems(result.recommendItems);
      setNewsLoading(false);
    });

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHome, newsDate]);

  // 활성 카테고리(상단 메뉴 on 상태):
  // - 카테고리 페이지: URL 파라미터(:corner)
  // - 기사 상세: 카테고리 리스트에서 진입한 경우 쿼리스트링의 categorySeq 유지
  //   (카테고리 키 = String(categorySeq) 이므로 그대로 매칭 가능. 없으면 '전체')
  const activeCorner = isCategory
    ? params.corner
    : isArticle
    ? searchParams.get("categorySeq")
    : null;
  // 검색어: 검색 결과 페이지 + (검색에서 진입한) 기사 상세에서도 유지 → 상단 검색란 입력값 보존
  const query = isSearch || isArticle ? searchParams.get("q") || "" : "";

  /* ---- 경로 이동 핸들러 ---- */
  // 상세 진입 시 리스트 컨텍스트(postSearchType + categorySeq)를 쿼리스트링으로 전달
  // → 상세의 postPrevNext(함께 읽어요)가 같은 컨텍스트로 이전/다음 기사를 조회.
  // categorySeq는 카테고리 리스트(searchCategorySeq)일 때만 전달.
  const onOpen = (a) => {
    if (!a || a.id == null) return;
    const qs = new URLSearchParams();
    if (a.postSearchType) qs.set("postSearchType", a.postSearchType);
    if (a.searchCategorySeq != null) qs.set("categorySeq", a.searchCategorySeq);
    if (a.searchQuery) qs.set("q", a.searchQuery); // 검색 리스트 → 상세: 검색어 유지
    const q = qs.toString();
    // "목록으로" 버튼이 (직전 기사가 아니라) 원래 리스트로 돌아가도록
    // 진입한 리스트 경로를 history state(listPath)에 실어 보낸다.
    //  - 리스트에서 진입: 현재 경로가 그 리스트
    //  - 기사 → "함께 읽어요" → 기사: 기존 listPath를 그대로 이어받음
    // 히스토리는 계속 push 하므로 브라우저 뒤로가기(백스페이스)는 직전 기사로 정상 동작한다.
    const listPath = isArticle
      ? location.state?.listPath
      : `${location.pathname}${location.search}`;
    navigate(`/kizda_news/article/${a.id}${q ? `?${q}` : ""}`, { state: { listPath } });
  };
  // 상세에서 받아온 실제 조회수(views)를 캐시(홈/PICK + 코너/카테고리/검색/오늘)의
  // 해당 기사에 그대로 설정 → 목록으로 돌아가도 상세와 동일한 값이 보인다.
  // (+1 추정이 아니라 서버 값 동기화이므로 동시 조회 +2 상황에서도 상세=목록 일치)
  const syncViewCount = (postSeq, views) => {
    if (views == null) return;
    const id = String(postSeq);
    const setViews = (arr) =>
      Array.isArray(arr)
        ? arr.map((a) => (String(a?.id) === id ? { ...a, views } : a))
        : arr;
    for (const [date, v] of _homeCache.byDate.entries()) {
      _homeCache.byDate.set(date, {
        newsItems: setViews(v.newsItems),
        popularItems: setViews(v.popularItems),
        recommendItems: setViews(v.recommendItems),
      });
    }
    setViewCountInCache(postSeq, views); // news_components의 _kdCache
  };
  const goHome = () => navigate("/");
  const handleCorner = (k) => navigate(`/kizda_news/category/${k}`);
  const handleTodayMore = (d) => navigate(`/kizda_news/today/${d}`);
  const handleSearch = (q) => {
    const t = (q || "").trim();
    navigate(t ? `/kizda_news/search?q=${encodeURIComponent(t)}` : "/kizda_news");
  };
  // "목록으로" 버튼: 기억해둔 진입 리스트(listPath)로 이동.
  // (함께 읽어요로 기사를 옮겨다녀도 항상 원래 리스트로 복귀)
  const goBack = () => {
    const listPath = location.state?.listPath;
    if (listPath) navigate(listPath);
    else if (window.history.length > 1) navigate(-1);
    else goHome();
  };

  /* ---- 카테고리 ↔ 기사 매칭 (이름 기준) ---- */
  const itemsOfCategory = (cat) =>
    cat ? articles.filter((a) => articleCategoryName(a) === normalizeCat(cat.name)) : [];

  /* ---- 파생 데이터 ---- */
  // 정적 기사 우선 조회(빠른 경로). 없으면 API(postsBySeq)로 조회
  const staticArticle = isArticle ? articles.find((a) => a.id === params.id) : null;
  const related = useMemo(
    () => (staticArticle ? articles.filter((a) => a.id !== staticArticle.id && a.corner === staticArticle.corner).slice(0, 2) : []),
    [staticArticle, articles]
  );

  // 기사 상세 진입 처리 (API 기사):
  //  1) 웹 조회수 기록(createWebPostRead) — 기사당 1회(StrictMode/재마운트 중복 방지).
  //     기록을 먼저 끝낸 뒤 상세를 받아 내 조회까지 포함된 값을 표시.
  //  2) 상세 조회(getNewsDetail) — 정적(시드) 기사는 서버 글이 아니므로 제외.
  //  3) 상세에서 받은 실제 조회수(views=webReadCount)를 목록 캐시에 그대로 동기화
  //     → 뒤로가기·"목록으로"·메뉴 어디로 돌아가도 목록 == 상세 (동시 조회 +2여도 일치).
  // KizdaNewsPage는 언마운트되지 않으므로 상세 이탈 시 ref를 초기화해 재진입 시 다시 기록.
  const loggedReadIdRef = useRef(null);
  const [apiArticle, setApiArticle] = useState(null);
  const [articleLoading, setArticleLoading] = useState(false);
  useEffect(() => {
    if (!isArticle) loggedReadIdRef.current = null; // 상세 이탈 → 다음 진입 시 다시 기록
    if (!isArticle || staticArticle) {
      setApiArticle(null);
      setArticleLoading(false);
      return;
    }
    let alive = true;
    setApiArticle(null);
    setArticleLoading(true);

    (async () => {
      const seq = parseInt(params.id, 10);
      // 조회수 기록(기사당 1회) → 기록 후 상세 조회
      if (!Number.isNaN(seq) && loggedReadIdRef.current !== seq) {
        loggedReadIdRef.current = seq;
        await createWebPostRead(seq);
      }
      const d = await getNewsDetail(params.id);
      if (!alive) return;
      setApiArticle(d);
      setArticleLoading(false);
      // 상세의 실제 조회수를 목록 캐시에 반영 → 목록과 상세가 일치
      if (d && d.views != null && !Number.isNaN(seq)) syncViewCount(seq, d.views);
    })();

    return () => {
      alive = false;
    };
  }, [isArticle, staticArticle, params.id]);

  const openArticle = staticArticle || apiArticle;

  /* ---- "이 기사와 함께 읽어요" (API 기사용) ----
     리스트에서 넘어온 컨텍스트(?postSearchType=…&categorySeq=…)와 동일한 조건의
     postPrevNext로 이전/다음 기사를 조회해 [이전, 다음] 순으로 표시.
     컨텍스트 없이 직접 진입하면 NEWS_ALL 기준. 정적 기사는 기존 related 사용 */
  const [apiRelated, setApiRelated] = useState([]);
  useEffect(() => {
    if (!isArticle || !apiArticle) {
      setApiRelated([]);
      return;
    }
    let alive = true;
    const ctx = {
      postSearchType: searchParams.get("postSearchType") || "NEWS_ALL",
      categorySeq: searchParams.get("categorySeq"),
    };
    const q = searchParams.get("q"); // 검색 컨텍스트면 이전/다음 기사에도 검색어 유지
    getPostPrevNext(apiArticle.id, ctx).then((pn) => {
      if (!alive) return;
      const items = pn ? [pn.prev, pn.next].filter(Boolean) : [];
      setApiRelated(q ? items.map((x) => ({ ...x, searchQuery: q })) : items);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isArticle, apiArticle]);

  const openRelated = staticArticle ? related : apiRelated;

  const activeCategory = isCategory ? categories.find((c) => c.key === activeCorner) : null;
  // 정적 폴백용(카테고리 페이지가 API 조회 실패 시 사용)
  const cornerItems = activeCategory ? itemsOfCategory(activeCategory) : [];

  /* ---- 본문 분기 ---- */
  let body;
  if (isArticle) {
    body = openArticle ? (
      <ArticlePage a={openArticle} related={openRelated} onOpen={onOpen} onBack={goBack} />
    ) : articleLoading ? (
      <section style={{ padding: "70px 0", textAlign: "center", color: "var(--muted)", fontSize: 15 }}>불러오는 중…</section>
    ) : (
      <NotFoundBody label="기사" onBack={goHome} />
    );
  } else if (isSearch) {
    body = <SearchResults query={query.trim()} onOpen={onOpen} onClear={goHome} />;
  } else if (isTodayAll) {
    body = <TodayAllPage date={params.date} articles={articles} onOpen={onOpen} onBack={goHome} />;
  } else if (isCategory) {
    body = activeCategory ? (
      <CornerPage corner={activeCategory} items={cornerItems} onOpen={onOpen} onBack={goHome} />
    ) : (
      <NotFoundBody label="카테고리" onBack={goHome} />
    );
  } else {
    body = (
      <>
        <TopAdBanner banners={landingBanners} loading={landingBannersLoading} />
        <TodayNews
          articles={articles}
          newsItems={newsItems}
          popularItems={popularItems}
          recommendItems={recommendItems}
          banners={newsBanners}
          loading={newsLoading}
          date={newsDate}
          onDateChange={setNewsDate}
          onOpen={onOpen}
          onMore={handleTodayMore}
        />
        {/* 카테고리별 섹션: categoryList 순서대로, 각 섹션이 자기 카테고리 뉴스를 API로 조회.
            카테고리 목록 로딩 완료 후에만 렌더(정적 키로 미리 호출되는 중복/null 방지) */}
        {categoriesReady && (
          <div style={{ display: "grid", gap: 48, marginTop: 48 }}>
            {categories.map((corner) => (
              <ApiCornerBlock key={corner.key} corner={corner} onOpen={onOpen} onMore={handleCorner} />
            ))}
          </div>
        )}
        <SubscribeCTA />
      </>
    );
  }

  return (
    <div className="kizda-news-page">
      <Header variant="subpage" />

      {/* 본문 (프로토타입 디자인 스코프) */}
      <div className="kizda-news-body">
        <CornerNav
          corners={categories}
          active={activeCorner}
          onPick={handleCorner}
          onHome={goHome}
          query={query}
          onSearch={handleSearch}
        />

        <main className="wrap" style={{ paddingBottom: 20 }}>{body}</main>

        {isHome && <SideAdRail banners={landingBanners} loading={landingBannersLoading} />}
      </div>

      <NewsFooter />
    </div>
  );
}

export default KizdaNewsPage;
